import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Cookie-based auth: the JWT lives in an httpOnly cookie (XSS-safe).
// withCredentials makes axios send it on every request.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Refresh the auth cookie every 12h while the app is open (token TTL is 24h)
const REFRESH_INTERVAL_MS = 12 * 60 * 60 * 1000;
const LEGACY_TOKEN_KEY = 'wellness_token';

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null); // in-memory only (e.g. for feed URLs); never persisted
    const [loading, setLoading] = useState(true);

    const logout = useCallback(async () => {
        try {
            await axios.post(`${API}/auth/logout`);
        } catch (_e) { /* cookie clear is best-effort */ }
        localStorage.removeItem(LEGACY_TOKEN_KEY);
        localStorage.removeItem('bestbuddy_needs_onboarding');
        localStorage.removeItem('bestbuddy_user_name');
        localStorage.removeItem('bestbuddy_onboarding_complete');
        delete axios.defaults.headers.common['Authorization'];
        setToken(null);
        setUser(null);
        window.location.reload();
    }, []);

    const refreshToken = useCallback(async () => {
        try {
            const response = await axios.post(`${API}/auth/refresh`);
            setToken(response.data.access_token);
            return response.data.access_token;
        } catch (e) {
            return null;
        }
    }, []);

    const fetchUser = useCallback(async () => {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        return response.data;
    }, []);

    // Bootstrap session: cookie first; migrate legacy localStorage tokens once.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await fetchUser(); // succeeds if the httpOnly cookie is valid
                if (!cancelled) refreshToken(); // populate in-memory token for feed URLs
            } catch (_err) {
                const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
                if (legacy) {
                    try {
                        // Exchange the old localStorage JWT for an httpOnly cookie
                        const res = await axios.post(`${API}/auth/refresh`, null, {
                            headers: { Authorization: `Bearer ${legacy}` },
                        });
                        if (!cancelled) {
                            setToken(res.data.access_token);
                            setUser(res.data.user);
                        }
                    } catch (_e2) {
                        // Legacy token invalid/expired — clean logout state
                    } finally {
                        localStorage.removeItem(LEGACY_TOKEN_KEY);
                        delete axios.defaults.headers.common['Authorization'];
                    }
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [fetchUser, refreshToken]);

    // Periodic sliding-session refresh while app is open
    useEffect(() => {
        if (!user) return undefined;
        const id = setInterval(() => { refreshToken(); }, REFRESH_INTERVAL_MS);
        return () => clearInterval(id);
    }, [user, refreshToken]);

    // Axios response interceptor: on expired token, refresh the cookie once and retry
    useEffect(() => {
        const interceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const original = error.config || {};
                const status = error.response?.status;
                const detail = error.response?.data?.detail;
                const isAuthEndpoint = (original.url || '').includes('/auth/login')
                    || (original.url || '').includes('/auth/register')
                    || (original.url || '').includes('/auth/refresh');
                if (status === 401 && detail === 'Token expired' && !original._retry && !isAuthEndpoint) {
                    original._retry = true;
                    const fresh = await refreshToken();
                    if (fresh) {
                        return axios(original); // cookie was renewed server-side
                    }
                    logout();
                }
                return Promise.reject(error);
            }
        );
        return () => axios.interceptors.response.eject(interceptor);
    }, [refreshToken, logout]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, user: userData } = response.data;
        localStorage.removeItem(LEGACY_TOKEN_KEY);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (email, password, name) => {
        const response = await axios.post(`${API}/auth/register`, { email, password, name });
        const { access_token, user: userData } = response.data;
        localStorage.setItem('bestbuddy_needs_onboarding', 'true');
        localStorage.setItem('bestbuddy_user_name', name);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const updateProfile = async (updates) => {
        try {
            const response = await axios.patch(`${API}/auth/profile`, updates);
            setUser(response.data);
            return response.data;
        } catch (error) {
            console.error('Profile update failed:', error.response?.data || error.message);
            throw error;
        }
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, login, register, logout, updateProfile, fetchUser, refreshToken }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
